"use client"

import type React from "react"
import { useState, useEffect, useRef, useCallback } from "react"
import { Box, Skeleton } from "@mui/material"

interface ImageOptimizerProps {
    src: string
    alt: string
    width?: string | number
    height?: string | number
    objectFit?: "cover" | "contain" | "fill" | "none" | "scale-down"
    style?: React.CSSProperties
    className?: string
    maxRetries?: number
}

const ImageOptimizer: React.FC<ImageOptimizerProps> = ({
    src,
    alt,
    width = "100%",
    height = "100%",
    objectFit = "cover",
    style,
    className,
    maxRetries = 3,
}) => {
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)
    const [imgSrc, setImgSrc] = useState<string | null>(null)
    const [retryCount, setRetryCount] = useState(0)
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null)

    const loadImage = useCallback(() => {
        setLoading(true)
        setError(false)

        // Clear any existing timeout
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current)
            retryTimeoutRef.current = null
        }

        // Create a new image for preloading
        const img = new Image()
        img.src = src

        img.onload = () => {
            setImgSrc(src)
            setLoading(false)
            setRetryCount(0) // Reset retry count on successful load
        }

        img.onerror = () => {
            // If we haven't reached max retries, schedule another attempt
            if (retryCount < maxRetries) {
                setRetryCount((prevCount) => prevCount + 1)

                // Exponential backoff: 1s, 2s, 4s, etc.
                const delay = Math.pow(2, retryCount) * 1000

                retryTimeoutRef.current = setTimeout(() => {
                    loadImage()
                }, delay)
            } else {
                setError(true)
                setLoading(false)
            }
        }
    }, [src, retryCount, maxRetries])

    useEffect(() => {
        loadImage()

        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current)
            }
        }
    }, [loadImage])

    if (loading) {
        return (
            <Skeleton
                variant="rectangular"
                width={width}
                height={height}
                animation="wave"
                sx={{
                    bgcolor: "rgba(0, 0, 0, 0.1)",
                    borderRadius: 1,
                    ...style,
                }}
                className={className}
            />
        )
    }

    if (error) {
        return (
            <Box
                sx={{
                    width,
                    height,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "rgba(0, 0, 0, 0.05)",
                    color: "text.secondary",
                    fontSize: "0.875rem",
                    borderRadius: 1,
                    ...style,
                }}
                className={className}
            >
                Error al cargar la imagen
            </Box>
        )
    }

    return (
        <img
            src={imgSrc || ""}
            alt={alt}
            style={{
                width,
                height,
                objectFit,
                ...style,
            }}
            className={className}
            loading="lazy"
        />
    )
}

export default ImageOptimizer

